import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Calendar, Send, RefreshCw, BarChart3, ChevronDown, ChevronUp, Download, Receipt } from 'lucide-react';

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedReport, setExpandedReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Tax Report state
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [taxMonth, setTaxMonth] = useState(defaultMonth);
  const [taxData, setTaxData] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const [taxDownloading, setTaxDownloading] = useState(false);

  const fetchData = async () => {
    try {
      const [rRes, nRes] = await Promise.all([
        api.get('/reports', { params: typeFilter !== 'all' ? { type: typeFilter } : {} }),
        api.get('/notifications', { params: { limit: 20 } })
      ]);
      setReports(rRes.data);
      setNotifications(nRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [typeFilter]);

  const handleGenerate = async (type) => {
    setGenerating(true);
    try {
      await api.post(`/reports/generate/${type}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} report generated`);
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };


  const toggleExpand = async (reportId) => {
    if (expandedReport?.id === reportId) {
      setExpandedReport(null);
      return;
    }
    try {
      const { data } = await api.get(`/reports/${reportId}`);
      setExpandedReport(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const loadTaxReport = async () => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(taxMonth)) {
      toast.error('Enter a valid month in YYYY-MM format'); return;
    }
    setTaxLoading(true);
    try {
      const { data } = await api.get('/finance/tax-report', { params: { month: taxMonth } });
      setTaxData(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setTaxLoading(false); }
  };

  const downloadTaxReportPdf = async () => {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(taxMonth)) {
      toast.error('Enter a valid month in YYYY-MM format'); return;
    }
    setTaxDownloading(true);
    try {
      const resp = await api.get('/finance/tax-report/pdf', {
        params: { month: taxMonth },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `quantora-tax-report-${taxMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Tax report downloaded');
    } catch (err) {
      // Blob error bodies need special parsing
      let msg = 'Failed to download';
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) {
          const parsed = JSON.parse(txt);
          msg = parsed.detail || msg;
        }
      } catch (_) {}
      toast.error(msg);
    }
    finally { setTaxDownloading(false); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Reports</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Daily and monthly P&L reports with auto-sharing</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => handleGenerate('daily')} disabled={generating} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="generate-daily-btn">
            <RefreshCw size={16} className={`mr-2 ${generating ? 'animate-spin' : ''}`} /> Generate Daily
          </Button>
          <Button onClick={() => handleGenerate('monthly')} disabled={generating} className="bg-brand-secondary hover:bg-brand-secondary-hover text-white rounded-sm" data-testid="generate-monthly-btn">
            <BarChart3 size={16} className="mr-2" /> Generate Monthly
          </Button>
        </div>
      </div>

      {/* Cron Status */}
      <Card className="border border-brand-border bg-white">
        <CardContent className="p-4 flex items-center gap-4 flex-wrap">
          <Badge className="bg-brand-success/15 text-brand-success border-0 px-3 py-1 text-xs">Cron Active</Badge>
          <span className="text-sm text-brand-text-secondary">Daily report: 12:00 AM IST</span>
          <span className="text-sm text-brand-text-secondary">Monthly report: 1st of month, 12:00 AM IST</span>
          <Badge className="bg-amber-100 text-amber-700 border-0 px-3 py-1 text-xs" data-testid="client-emails-off-badge">Client emails: OFF</Badge>
        </CardContent>
      </Card>

      {/* ─── Monthly Tax & Profit Report ─── */}
      <Card className="border border-brand-border bg-white" data-testid="tax-report-section">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-brand-text-primary flex items-center gap-2">
            <Receipt size={18} className="text-brand-primary" /> Monthly Tax & Profit Report
          </CardTitle>
          <p className="text-xs text-brand-text-secondary mt-1">
            Per-client profit split for tax reporting · Client 50% · STT 10% · Agent 10% · Company 30%
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-brand-text-secondary mb-1 block">Month (YYYY-MM)</label>
              <Input
                type="month"
                value={taxMonth}
                onChange={(e) => setTaxMonth(e.target.value)}
                className="w-44 bg-white border-brand-border rounded-sm"
                data-testid="tax-month-input"
              />
            </div>
            <Button
              onClick={loadTaxReport}
              disabled={taxLoading}
              variant="outline"
              className="rounded-sm border-brand-border"
              data-testid="load-tax-report-btn"
            >
              <RefreshCw size={14} className={`mr-2 ${taxLoading ? 'animate-spin' : ''}`} />
              {taxLoading ? 'Loading...' : 'Preview'}
            </Button>
            <Button
              onClick={downloadTaxReportPdf}
              disabled={taxDownloading}
              className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm"
              data-testid="download-tax-report-pdf-btn"
            >
              <Download size={14} className="mr-2" />
              {taxDownloading ? 'Preparing PDF...' : 'Export PDF'}
            </Button>
          </div>

          {taxData && (
            <div className="space-y-3" data-testid="tax-report-preview">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-sm border border-brand-border bg-brand-bg/40 p-3">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-brand-text-secondary">Active Clients</p>
                  <p className="text-lg font-semibold text-brand-text-primary">{taxData.client_count}</p>
                </div>
                <div className="rounded-sm border border-brand-border bg-brand-bg/40 p-3">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-brand-text-secondary">Period Profit</p>
                  <p className="text-lg font-semibold text-brand-success">{Number(taxData.totals?.period_profit || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-sm border border-brand-border bg-brand-bg/40 p-3">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-brand-text-secondary">Period Loss</p>
                  <p className="text-lg font-semibold text-brand-error">{Number(taxData.totals?.period_loss || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-sm border border-brand-border bg-brand-bg/40 p-3">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-brand-text-secondary">Net P&L</p>
                  <p className={`text-lg font-semibold ${Number(taxData.totals?.net_pnl || 0) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{Number(taxData.totals?.net_pnl || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-sm border border-brand-border bg-brand-bg/40 p-3">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-brand-text-secondary">Client Share (50%)</p>
                  <p className="text-lg font-semibold text-brand-text-primary">{Number(taxData.totals?.client_share || 0).toLocaleString()}</p>
                </div>
              </div>

              <div className="overflow-x-auto border border-brand-border rounded-sm">
                <table className="w-full text-xs" data-testid="tax-report-table">
                  <thead className="bg-brand-bg/60">
                    <tr className="border-b border-brand-border">
                      {['#', 'Client', 'Account ID', 'Net Deposit', 'Balance', 'Period Profit', 'Period Loss', 'Net P&L', 'Client 50%', 'STT 10%', 'Agent 10%', 'Company 30%'].map(h => (
                        <th key={h} className="text-left py-2 px-3 uppercase tracking-[0.12em] font-semibold text-brand-text-secondary whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {taxData.rows.length === 0 ? (
                      <tr><td colSpan={12} className="py-6 text-center text-brand-text-secondary">No active clients found for this month</td></tr>
                    ) : taxData.rows.map((r, i) => (
                      <tr key={r.client_id || `${r.name}-${i}`} className="border-b border-brand-border/40 hover:bg-brand-surface-hover transition-colors">
                        <td className="py-2 px-3 text-brand-text-secondary">{i + 1}</td>
                        <td className="py-2 px-3 text-brand-text-primary font-medium">{r.name}</td>
                        <td className="py-2 px-3 font-mono text-brand-text-secondary">{r.client_id}</td>
                        <td className="py-2 px-3 text-right text-brand-text-secondary">{Number(r.net_deposit).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-text-secondary">{Number(r.current_balance).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-success">{Number(r.period_profit).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-error">{Number(r.period_loss).toLocaleString()}</td>
                        <td className={`py-2 px-3 text-right font-medium ${Number(r.net_pnl) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{Number(r.net_pnl).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-text-primary">{Number(r.client_share).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-text-primary">{Number(r.stt_charge).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-text-primary">{Number(r.agent_commission).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-text-primary">{Number(r.company_share).toLocaleString()}</td>
                      </tr>
                    ))}
                    {taxData.rows.length > 0 && (
                      <tr className="bg-indigo-50/60 font-semibold border-t-2 border-brand-border" data-testid="tax-report-totals-row">
                        <td className="py-2 px-3"></td>
                        <td className="py-2 px-3 text-brand-text-primary">TOTALS</td>
                        <td className="py-2 px-3 text-brand-text-secondary">{taxData.client_count} clients</td>
                        <td className="py-2 px-3 text-right">{Number(taxData.totals.net_deposit).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right">{Number(taxData.totals.current_balance).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-success">{Number(taxData.totals.period_profit).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-brand-error">{Number(taxData.totals.period_loss).toLocaleString()}</td>
                        <td className={`py-2 px-3 text-right ${Number(taxData.totals.net_pnl) >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>{Number(taxData.totals.net_pnl).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right">{Number(taxData.totals.client_share).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right">{Number(taxData.totals.stt_charge).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right">{Number(taxData.totals.agent_commission).toLocaleString()}</td>
                        <td className="py-2 px-3 text-right">{Number(taxData.totals.company_share).toLocaleString()}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex gap-2 items-center">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 bg-white border-brand-border rounded-sm" data-testid="report-type-filter">
            <SelectValue placeholder="Filter type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reports</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reports List */}
      <div className="space-y-3">
        {loading ? (
          <Card className="border border-brand-border bg-white">
            <CardContent className="p-12 text-center text-brand-text-secondary">Loading reports...</CardContent>
          </Card>
        ) : reports.length === 0 ? (
          <Card className="border border-brand-border bg-white">
            <CardContent className="p-12 text-center text-brand-text-secondary">
              No reports generated yet. Click "Generate Daily" or "Generate Monthly" to create one.
            </CardContent>
          </Card>
        ) : reports.map(r => (
          <Card key={r.id} className="border border-brand-border bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${r.type === 'daily' ? 'bg-brand-primary/10' : 'bg-brand-secondary/10'}`}>
                    {r.type === 'daily' ? <Calendar size={20} className="text-brand-primary" /> : <BarChart3 size={20} className="text-brand-secondary" />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-brand-text-primary capitalize">{r.type} Report</p>
                      <Badge className="text-xs border-0 bg-brand-bg text-brand-text-secondary">{r.date}</Badge>
                    </div>
                    <p className="text-xs text-brand-text-secondary">
                      {r.client_count} clients | Generated: {r.generated_at ? new Date(r.generated_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => toggleExpand(r.id)} className="text-xs" data-testid={`expand-report-${r.id}`}>
                    {expandedReport?.id === r.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </Button>
                </div>
              </div>

              {/* Expanded Report Details */}
              {expandedReport?.id === r.id && expandedReport.data && (
                <div className="mt-4 border-t border-brand-border pt-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-brand-border">
                          {['Client', 'Profit', 'Loss', 'Net P&L', ...(r.type === 'monthly' ? ['Deposits', 'Withdrawals'] : []), 'Balance'].map(h => (
                            <th key={h} className="text-left py-2 px-3 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {expandedReport.data.map((item, i) => (
                          <tr key={item.id || item.client_id || `${item.client_name}-${i}`} className="border-b border-brand-border/30">
                            <td className="py-2 px-3 text-brand-text-primary">{item.client_name}</td>
                            <td className="py-2 px-3 text-brand-success">${Number(item.total_profit || 0).toLocaleString()}</td>
                            <td className="py-2 px-3 text-brand-error">${Number(item.total_loss || 0).toLocaleString()}</td>
                            <td className={`py-2 px-3 font-medium ${item.net_pnl >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${Number(item.net_pnl || 0).toLocaleString()}</td>
                            {r.type === 'monthly' && (
                              <>
                                <td className="py-2 px-3 text-brand-text-secondary">${Number(item.deposits || 0).toLocaleString()}</td>
                                <td className="py-2 px-3 text-brand-text-secondary">${Number(item.withdrawals || 0).toLocaleString()}</td>
                              </>
                            )}
                            <td className="py-2 px-3 font-medium text-brand-text-primary">${Number(item.current_balance || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Notification Log */}
      <Card className="border border-brand-border bg-white">
        <CardHeader><CardTitle className="font-heading text-lg text-brand-text-primary flex items-center gap-2"><Send size={18} /> Notification Log (MOCKED)</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="notifications-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Time', 'Type', 'Recipient', 'Subject', 'Status'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {notifications.length === 0 ? (
                  <tr><td colSpan={5} className="py-8 text-center text-brand-text-secondary">No notifications yet</td></tr>
                ) : notifications.map((n, i) => (
                  <tr key={n.id || n._id || `${n.created_at}-${i}`} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-2 px-4 text-brand-text-secondary text-xs">{n.created_at ? new Date(n.created_at).toLocaleString() : '-'}</td>
                    <td className="py-2 px-4"><Badge className="text-xs border-0 bg-brand-bg text-brand-text-secondary">{n.type}</Badge></td>
                    <td className="py-2 px-4 text-brand-text-primary text-xs">{n.recipient}</td>
                    <td className="py-2 px-4 text-brand-text-secondary text-xs max-w-[250px] truncate">{n.subject || n.content}</td>
                    <td className="py-2 px-4"><Badge className="text-xs border-0 bg-amber-100 text-amber-700">{n.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
